import React, { useMemo } from "react";
import { useTheme } from "@mui/material";
import TrutoIntegrationConnectionDrawer from "./TrutoIntegrationConnectionDrawer";

const GITHUB_FULL = "github";
const GITHUB_NO_CODE = "github_no_code_access";

const GitHubConnectionDrawer = ({
  open,
  onClose,
  integrationLabel,
  integrationLogo,
  refetchIntegrationDetails,
  onError,
}) => {
  const theme = useTheme();

  const variants = useMemo(
    () => [
      {
        key: GITHUB_FULL,
        integrationKey: GITHUB_FULL,
        label: "GitHub (With Code Security)",
        description:
          "Enables the Code Security module for secret scanning, along with repository and branch metadata, and organization members.",
        recommended: true,
        default: true,
      },
      {
        key: GITHUB_NO_CODE,
        integrationKey: GITHUB_NO_CODE,
        label: "GitHub (Without Code Security)",
        description:
          "Repository and branch metadata, and organization members only. The Code Security module is not available.",
      },
    ],
    []
  );

  const config = useMemo(
    () => ({
      heading: "Which option should I pick?",
      subtitle:
        "**Pick With Code Security** unless your organization's policy does not allow it.",
      steps: [
        "**With Code Security (recommended).** Connects through GitHub's standard OAuth flow and syncs repository and branch metadata, and organization members.\n\nEnables the [**Code Security module** ↗](/admin/code-security), which scans your source for security issues such as leaked secrets and other vulnerabilities. Scans run on demand and findings are stored as redacted previews only, never the raw code.\n\nCode is **only scanned for repos you mark In Scope** in Code Security. Everything else is ignored.\n\n**Repository and branch metadata** power the branch protection check (verify that main / production branches require reviews, status checks, and block force pushes).\n\n**Organization members** power the Access Review module, so you can review who has access to each repo.\n\nIf you want to limit the scope to specific repositories instead of the whole org, on the next screen **select `JWT` as the connection method** instead of OAuth. This connects through our restricted GitHub App. **Install it on the organization (not a personal account)** and tick only the repos you want to include.\n\n**Access granted:** OAuth flow — `read:user` (profile), `read:org` (org + members), `repo` (repo list, branches, code). JWT (restricted App) flow — `Metadata: Read` (repo + branch list), `Members: Read` (org members), `Contents: Read` (code).",
        "**Without Code Security.** Connects through a restricted GitHub App that must be **installed on the organization (not a personal account)**. Syncs repository and branch metadata, and organization members only.\n\n**Repository and branch metadata** power the branch protection check. **Organization members** power the Access Review module.\n\nPick this only if your organization's policy does not allow Code Security. The [**Code Security module** ↗](/admin/code-security) will not be available.\n\n**Access granted:** `Metadata: Read` (repo + branch list), `Members: Read` (org members).",
      ],
      footerText:
        "You can disconnect any option at any time from the Manage screen. GitHub App installations are per-organization. Install on each org you want to sync.",
      stepsCollapsible: false,
      stepsBodyColor: theme.customColors.text.strong,
    }),
    [theme]
  );

  return (
    <TrutoIntegrationConnectionDrawer
      open={open}
      onClose={onClose}
      integrationKey={GITHUB_FULL}
      integrationLabel={integrationLabel || "GitHub"}
      integrationLogo={integrationLogo}
      refetchIntegrationDetails={refetchIntegrationDetails}
      onError={onError}
      variants={variants}
      config={config}
    />
  );
};

export default GitHubConnectionDrawer;
